import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  TrendingUp, TrendingDown, AlertTriangle, Sparkles, DollarSign,
  Wallet, ArrowUpRight, ArrowDownRight, Package, Search, Calendar,
  CheckCircle2, XCircle,
} from 'lucide-react'
import { api, formatMoney } from '@/lib/api'

function InsightCard({ tipo, titulo, valor, obs }: any) {
  const cores: any = {
    good: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    warning: 'border-amber-200 bg-amber-50 text-amber-900',
    alert: 'border-red-200 bg-red-50 text-red-900',
  }
  const Icon = tipo === 'alert' ? AlertTriangle : tipo === 'warning' ? AlertTriangle : CheckCircle2
  return (
    <div className={`relative group border rounded p-2.5 cursor-help ${cores[tipo] || cores.warning}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase font-semibold opacity-80">
        <Icon size={10} />{titulo}
      </div>
      <div className="text-base font-bold font-mono mt-0.5">{valor}</div>
      <div className="absolute z-50 left-0 top-full mt-1 hidden group-hover:block bg-ink-950 text-white text-[11px] rounded px-3 py-2 max-w-xs whitespace-pre-wrap shadow-lg pointer-events-none">
        {obs}
      </div>
    </div>
  )
}

function KpiCard({ label, valor, subtitulo, cor = 'blue', tooltip, icone }: any) {
  const cores: any = {
    emerald: 'border-emerald-200 bg-emerald-50',
    blue: 'border-blue-200 bg-blue-50',
    violet: 'border-violet-200 bg-violet-50',
    amber: 'border-amber-200 bg-amber-50',
    red: 'border-red-200 bg-red-50',
    rose: 'border-rose-200 bg-rose-50',
  }
  const corText: any = {
    emerald: 'text-emerald-900', blue: 'text-blue-900', violet: 'text-violet-900',
    amber: 'text-amber-900', red: 'text-red-900', rose: 'text-rose-900',
  }
  return (
    <div className={`relative group border rounded-lg p-3 ${cores[cor] || cores.blue} ${tooltip ? 'cursor-help' : ''}`}>
      <div className={`flex items-center gap-1.5 text-[10px] font-semibold uppercase opacity-70 ${corText[cor]}`}>
        {icone}{label}
      </div>
      <div className={`text-lg font-bold font-mono mt-1 ${corText[cor]}`}>{typeof valor === 'number' ? formatMoney(valor) : valor}</div>
      {subtitulo && <div className={`text-[10px] mt-0.5 opacity-70 ${corText[cor]}`}>{subtitulo}</div>}
      {tooltip && (
        <div className="absolute z-50 left-0 top-full mt-1 hidden group-hover:block bg-ink-950 text-white text-[11px] rounded px-3 py-2 max-w-md whitespace-pre-wrap shadow-lg pointer-events-none">
          {tooltip}
        </div>
      )}
    </div>
  )
}

// =================================
// VISÃO DO DONO
// =================================
export function VisaoDonoPage() {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['visao-dono'],
    queryFn: async () => (await api.get('/dashboards/visao-dono')).data,
    refetchInterval: 60_000,
  })

  if (isLoading || !data) return <div className="p-12 text-center text-ink-400">Carregando...</div>

  const k = data.kpis
  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles size={22} className="text-violet-600" /> Visão do Dono
        </h1>
        <p className="text-xs text-ink-500">Tudo que importa em uma tela. Atualiza a cada 60s.</p>
      </div>

      {/* INSIGHTS */}
      {data.insights.length > 0 && (
        <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-violet-600" />
            <span className="text-xs font-semibold uppercase text-violet-900">Alertas e insights</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {data.insights.map((i: any, idx: number) => <InsightCard key={idx} {...i} />)}
          </div>
        </div>
      )}

      {/* KPIs principais */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <KpiCard
          label="Saldo em caixa" valor={k.saldo_caixa} cor="emerald"
          icone={<Wallet size={11} />}
          tooltip="Soma de todas contas bancárias ativas"
        />
        <KpiCard
          label="Receita mês" valor={k.receita_mes} cor="emerald"
          icone={<ArrowUpRight size={11} />}
          tooltip={`Lançamentos tipo receita do mês\nMargem: ${k.margem_pct.toFixed(1)}%`}
        />
        <KpiCard
          label="Despesa mês" valor={k.despesa_mes} cor="red"
          icone={<ArrowDownRight size={11} />}
        />
        <KpiCard
          label="Lucro mês" valor={k.lucro_mes} cor="violet"
          subtitulo={`${k.margem_pct.toFixed(1)}% margem`}
          icone={<DollarSign size={11} />}
          tooltip={`Receita ${formatMoney(k.receita_mes)}\n− Despesa ${formatMoney(k.despesa_mes)}\n= ${formatMoney(k.lucro_mes)}`}
        />
        <KpiCard
          label="A receber 30d" valor={k.a_receber_30d} cor="blue"
          subtitulo={`Total aberto: ${formatMoney(k.a_receber_total)}`}
          tooltip={`Vencidos: ${k.receber_vencidos_qtd} contas / ${formatMoney(k.receber_vencidos_valor)}`}
        />
        <KpiCard
          label="A pagar 30d" valor={k.a_pagar_30d} cor="amber"
          subtitulo={`Total aberto: ${formatMoney(k.a_pagar_total)}`}
          tooltip={`Vencidos: ${k.pagar_vencidos_qtd} contas / ${formatMoney(k.pagar_vencidos_valor)}`}
        />
      </div>

      {/* Estoque */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KpiCard
          label="Produtos ativos" valor={Number(k.estoque_qtd_produtos || 0).toLocaleString('pt-BR')} cor="blue"
          icone={<Package size={11} />}
          tooltip="Quantidade de produtos cadastrados (simples + variações)"
        />
        <KpiCard label="Estoque (custo)" valor={k.estoque_valor_custo} cor="violet" tooltip="Soma de qty × custo_medio dos simples/variação" />
        <KpiCard label="Estoque (venda)" valor={k.estoque_valor_venda} cor="emerald" tooltip="Soma de qty × preço de venda" />
        <KpiCard label="Margem potencial" valor={`${k.estoque_valor_venda > 0 ? ((k.estoque_valor_venda - k.estoque_valor_custo) / k.estoque_valor_venda * 100).toFixed(1) : 0}%`} cor="amber" />
      </div>

      {/* Top recebíveis e pagáveis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-ink-200 rounded-lg p-3">
          <h3 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-1.5">
            <ArrowUpRight size={14} /> Top 5 recebíveis abertos
          </h3>
          <div className="space-y-1">
            {data.top_receber.map((c: any) => (
              <div key={c.id} className={`flex justify-between p-2 rounded text-xs ${c.vencido ? 'bg-red-50' : 'hover:bg-emerald-50'}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{c.descricao}</div>
                  <div className="text-[10px] text-ink-500">Vence: {c.vencimento.slice(8,10)}/{c.vencimento.slice(5,7)}{c.vencido && <span className="text-red-600 ml-1">(vencido)</span>}</div>
                </div>
                <div className="font-mono font-semibold text-emerald-700">{formatMoney(c.valor)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-ink-200 rounded-lg p-3">
          <h3 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
            <ArrowDownRight size={14} /> Top 5 pagáveis abertos
          </h3>
          <div className="space-y-1">
            {data.top_pagar.map((c: any) => (
              <div key={c.id} className={`flex justify-between p-2 rounded text-xs ${c.vencido ? 'bg-red-50' : 'hover:bg-amber-50'}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{c.descricao}</div>
                  <div className="text-[10px] text-ink-500">Vence: {c.vencimento.slice(8,10)}/{c.vencimento.slice(5,7)}{c.vencido && <span className="text-red-600 ml-1">(vencido)</span>}</div>
                </div>
                <div className="font-mono font-semibold text-amber-700">{formatMoney(c.valor)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// =================================
// FLUXO DE CAIXA
// =================================
export function FluxoCaixaPage() {
  const [dias, setDias] = useState(90)
  const { data, isLoading } = useQuery<any>({
    queryKey: ['fluxo-caixa', dias],
    queryFn: async () => (await api.get('/dashboards/fluxo-caixa', { params: { dias } })).data,
  })

  if (isLoading || !data) return <div className="p-12 text-center text-ink-400">Carregando...</div>

  const maxAbs = Math.max(...data.serie.map((s: any) => Math.abs(s.entradas) + Math.abs(s.saidas)), 1)

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp size={22} className="text-blue-600" /> Fluxo de Caixa
          </h1>
          <p className="text-xs text-ink-500">Realizado (caixa, últimos 30 dias) + projeção de títulos pra frente. Vencidos consolidados em hoje.</p>
        </div>
        <select className="input w-[120px]" value={dias} onChange={e => setDias(Number(e.target.value))}>
          <option value={30}>30 dias</option>
          <option value={60}>60 dias</option>
          <option value={90}>90 dias</option>
          <option value={180}>180 dias</option>
        </select>
      </div>

      {data.insights.length > 0 && (
        <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-violet-600" />
            <span className="text-xs font-semibold uppercase">Alertas</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {data.insights.map((i: any, idx: number) => <InsightCard key={idx} {...i} />)}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiCard label="Saldo inicial" valor={data.saldo_inicial} cor="blue" tooltip="Soma das contas bancárias ativas" />
        <KpiCard label="Total entradas" valor={data.total_entradas} cor="emerald" />
        <KpiCard label="Total saídas" valor={data.total_saidas} cor="red" />
        <KpiCard label="Saldo final projetado" valor={data.saldo_final} cor="violet" />
        <KpiCard label="Saldo mínimo" valor={data.saldo_minimo} cor={data.saldo_minimo < 0 ? 'red' : 'amber'} subtitulo={`em ${data.saldo_minimo_data.slice(8,10)}/${data.saldo_minimo_data.slice(5,7)}`} />
      </div>

      <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-ink-100">
            <tr className="text-[10px] uppercase font-semibold text-ink-600">
              <th className="text-left py-2 pl-3">Data</th>
              <th className="text-right py-2">Entradas</th>
              <th className="text-right py-2">Saídas</th>
              <th className="text-right py-2 pr-3">Saldo Acumulado</th>
              <th className="py-2 px-3">Visualização</th>
            </tr>
          </thead>
          <tbody>
            {data.serie.filter((s: any) => s.entradas > 0 || s.saidas > 0 || s.data === data.serie[0].data).map((s: any) => {
              const ent_w = Math.round((s.entradas / maxAbs) * 100)
              const sai_w = Math.round((s.saidas / maxAbs) * 100)
              return (
                <tr key={s.data} className="border-t border-ink-100 hover:bg-blue-50/30">
                  <td className="py-1.5 pl-3 font-mono text-ink-600">
                    {s.data.slice(8,10)}/{s.data.slice(5,7)}
                    {/* FIX 30/06: marca realizado (caixa, passado) vs projetado (títulos) */}
                    <span className={`ml-1.5 text-[9px] px-1 rounded ${s.realizado ? 'bg-ink-100 text-ink-500' : 'bg-blue-50 text-blue-600'}`}>
                      {s.realizado ? 'real' : 'proj'}
                    </span>
                  </td>
                  <td className="text-right text-emerald-700 font-mono py-1.5">{s.entradas > 0 ? '+' + formatMoney(s.entradas) : '—'}</td>
                  <td className="text-right text-red-700 font-mono py-1.5">{s.saidas > 0 ? '−' + formatMoney(s.saidas) : '—'}</td>
                  <td className={`text-right font-mono font-semibold py-1.5 pr-3 ${s.saldo < 0 ? 'text-red-700' : 'text-ink-900'}`}>
                    {formatMoney(s.saldo)}
                  </td>
                  <td className="py-1.5 px-3 w-1/3">
                    <div className="flex items-center gap-1">
                      <div className="bg-emerald-400 h-2 rounded" style={{ width: `${ent_w}%` }} />
                      <div className="bg-red-400 h-2 rounded" style={{ width: `${sai_w}%` }} />
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// =================================
// POSIÇÃO DE ESTOQUE
// =================================
const FILTROS_ESTOQUE_INICIAIS = {
  filial: '', deposito: '', fornecedor: '', marca: '',
  ativo: 'true' as 'true' | 'false' | '',
  qtdMin: '', qtdMax: '', valorMin: '', valorMax: '',
  ordenarPor: 'valor_custo' as 'valor_custo' | 'valor_venda' | 'estoque' | 'codigo' | 'descricao',
  ordenarDir: 'desc' as 'asc' | 'desc',
}

export function PosicaoEstoquePage() {
  const [busca, setBusca] = useState('')
  const [filtros, setFiltros] = useState(FILTROS_ESTOQUE_INICIAIS)

  const params = useMemo(() => {
    const p: Record<string, string> = { ordenar_por: filtros.ordenarPor, ordenar_dir: filtros.ordenarDir }
    if (filtros.filial) p.filial_id = filtros.filial
    if (filtros.deposito) p.deposito_id = filtros.deposito
    if (filtros.fornecedor) p.fornecedor_id = filtros.fornecedor
    if (filtros.marca) p.marca = filtros.marca
    if (filtros.ativo) p.ativo = filtros.ativo
    if (filtros.qtdMin) p.qtd_min = filtros.qtdMin
    if (filtros.qtdMax) p.qtd_max = filtros.qtdMax
    if (filtros.valorMin) p.valor_min = filtros.valorMin
    if (filtros.valorMax) p.valor_max = filtros.valorMax
    return p
  }, [filtros])

  const { data, isLoading } = useQuery<any>({
    queryKey: ['posicao-estoque', params],
    queryFn: async () => (await api.get('/dashboards/posicao-estoque', { params })).data,
  })

  const { data: filiais = [] } = useQuery<any[]>({
    queryKey: ['filiais-posicao-estoque'],
    queryFn: async () => (await api.get('/filiais')).data,
  })
  const { data: depositos = [] } = useQuery<any[]>({
    queryKey: ['depositos-posicao-estoque'],
    queryFn: async () => (await api.get('/estoque/depositos')).data,
  })
  const { data: fornecedores = [] } = useQuery<any[]>({
    queryKey: ['fornecedores-posicao-estoque'],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { tipo: 'fornecedor' } })).data,
  })

  const depositosDaFilial = useMemo(
    () => depositos.filter((d: any) => !filtros.filial || String(d.filial_id) === filtros.filial),
    [depositos, filtros.filial],
  )

  const setFiltro = (campo: keyof typeof FILTROS_ESTOQUE_INICIAIS, valor: string) => {
    setFiltros(f => {
      const novo = { ...f, [campo]: valor }
      if (campo === 'filial') novo.deposito = ''
      return novo
    })
  }
  const limparFiltros = () => { setFiltros(FILTROS_ESTOQUE_INICIAIS); setBusca('') }
  const filtrosAtivos =
    Object.entries(filtros).filter(([k, v]) =>
      v && !(k === 'ativo' && v === 'true') && k !== 'ordenarPor' && k !== 'ordenarDir',
    ).length + (busca ? 1 : 0)

  const topFiltrado = useMemo(() => {
    if (!data) return []
    const q = busca.toLowerCase().trim()
    if (!q) return data.top_estoque_valor
    return data.top_estoque_valor.filter((p: any) =>
      (p.codigo || '').toLowerCase().includes(q) || (p.descricao || '').toLowerCase().includes(q)
    )
  }, [data, busca])

  const toggleSort = (campo: typeof FILTROS_ESTOQUE_INICIAIS['ordenarPor']) =>
    setFiltros(f => ({
      ...f,
      ordenarPor: campo,
      ordenarDir: f.ordenarPor === campo ? (f.ordenarDir === 'asc' ? 'desc' : 'asc') : 'desc',
    }))
  const arrow = (campo: string) =>
    filtros.ordenarPor === campo ? (filtros.ordenarDir === 'asc' ? ' ▲' : ' ▼') : ''

  if (isLoading || !data) return <div className="p-12 text-center text-ink-400">Carregando...</div>

  const k = data.kpis

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package size={22} className="text-orange-600" /> Posição de Estoque
          </h1>
          <p className="text-xs text-ink-500">
            Valor de estoque + alertas de ruptura e produtos sem custo
            {data.filtrado_por_local && <span className="ml-1 font-medium text-orange-700">— saldo da loja/depósito selecionado</span>}
          </p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-400" />
          <input className="input pl-7 w-[260px]" placeholder="Buscar SKU ou descrição..."
            value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
      </div>

      {data.insights.length > 0 && (
        <div className="bg-gradient-to-r from-violet-50 to-orange-50 border border-violet-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-violet-600" />
            <span className="text-xs font-semibold uppercase">Alertas e insights</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {data.insights.map((i: any, idx: number) => <InsightCard key={idx} {...i} />)}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <KpiCard label="Total produtos" valor={k.total_produtos} cor="blue" subtitulo={`${k.simples} simples + ${k.kit} kits + ${k.grade_variacao} grade/var`} />
        <KpiCard label="Com estoque" valor={k.com_estoque} cor="emerald" subtitulo={`${(k.qtd_unidades_total).toLocaleString('pt-BR')} unidades`} />
        <KpiCard label="Em ruptura" valor={data.ruptura_total} cor="red" tooltip="Produtos com estoque ≤ 0 (no filtro atual)" />
        <KpiCard label="Sem custo" valor={data.sem_custo_total} cor="amber" tooltip="Produtos com estoque mas custo zero — margem invisível" />
        <KpiCard label="Valor (custo)" valor={k.valor_custo_total} cor="violet" />
        <KpiCard label="Valor (venda)" valor={k.valor_venda_total} cor="emerald" subtitulo={`${k.margem_potencial_pct.toFixed(1)}% margem`} />
      </div>

      {/* FIX (09/09, pedido do cliente): filtros — antes só existia a busca por SKU/descrição */}
      <div className="bg-white border border-ink-200 rounded-lg p-3 space-y-2">
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-[10px] font-semibold text-ink-500 uppercase">Filial</label>
            <select className="input min-w-[150px]" value={filtros.filial} onChange={e => setFiltro('filial', e.target.value)}>
              <option value="">Todas (consolidado)</option>
              {filiais.map((f: any) => <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social || `Filial ${f.codigo}`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-ink-500 uppercase">Depósito</label>
            <select className="input min-w-[150px]" value={filtros.deposito} onChange={e => setFiltro('deposito', e.target.value)}>
              <option value="">Todos</option>
              {depositosDaFilial.map((d: any) => <option key={d.id} value={d.id}>{d.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-ink-500 uppercase">Fornecedor</label>
            <select className="input min-w-[160px]" value={filtros.fornecedor} onChange={e => setFiltro('fornecedor', e.target.value)}>
              <option value="">Todos</option>
              {fornecedores.map((f: any) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-ink-500 uppercase">Marca</label>
            <select className="input min-w-[130px]" value={filtros.marca} onChange={e => setFiltro('marca', e.target.value)}>
              <option value="">Todas</option>
              {(data.marcas_disponiveis || []).map((m: string) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-ink-500 uppercase">Ativo</label>
            <select className="input min-w-[100px]" value={filtros.ativo} onChange={e => setFiltro('ativo', e.target.value)}>
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
              <option value="">Todos</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-[10px] font-semibold text-ink-500 uppercase">Qtd. mínima</label>
            <input type="number" className="input w-24" value={filtros.qtdMin} onChange={e => setFiltro('qtdMin', e.target.value)} placeholder="0" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-ink-500 uppercase">Qtd. máxima</label>
            <input type="number" className="input w-24" value={filtros.qtdMax} onChange={e => setFiltro('qtdMax', e.target.value)} placeholder="—" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-ink-500 uppercase">Valor mín. (custo)</label>
            <input type="number" className="input w-28" value={filtros.valorMin} onChange={e => setFiltro('valorMin', e.target.value)} placeholder="R$ 0" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-ink-500 uppercase">Valor máx. (custo)</label>
            <input type="number" className="input w-28" value={filtros.valorMax} onChange={e => setFiltro('valorMax', e.target.value)} placeholder="—" />
          </div>
          {filtrosAtivos > 0 && (
            <button onClick={limparFiltros} className="text-xs px-2.5 py-1.5 rounded border border-ink-200 hover:bg-ink-50">Limpar filtros</button>
          )}
          <div className="text-[10px] text-ink-500 ml-auto">
            Mostrando {topFiltrado.length} de {data.total_itens_filtrados} produto(s) no filtro (máx. 100 na tabela)
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-ink-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-ink-50 border-b text-xs font-semibold uppercase">Resultados — {topFiltrado.length} itens</div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase font-semibold text-ink-600 border-b">
                <th onClick={() => toggleSort('codigo')} className="text-left py-1.5 pl-3 cursor-pointer select-none">SKU{arrow('codigo')}</th>
                <th onClick={() => toggleSort('descricao')} className="text-left py-1.5 cursor-pointer select-none">Produto{arrow('descricao')}</th>
                <th className="text-left py-1.5">Marca</th>
                <th onClick={() => toggleSort('estoque')} className="text-right py-1.5 cursor-pointer select-none">Qty{arrow('estoque')}</th>
                <th className="text-right py-1.5">Custo unit</th>
                <th onClick={() => toggleSort('valor_custo')} className="text-right py-1.5 cursor-pointer select-none">Valor total{arrow('valor_custo')}</th>
              </tr>
            </thead>
            <tbody>
              {topFiltrado.map((p: any) => (
                <tr key={p.id} className="border-t border-ink-100 hover:bg-blue-50/30">
                  <td className="py-1.5 pl-3 font-mono text-[10px]">{p.codigo}</td>
                  <td className="py-1.5 truncate max-w-[220px]">{p.descricao}</td>
                  <td className="py-1.5 text-ink-500">{p.marca || '—'}</td>
                  <td className="text-right py-1.5 font-mono">{p.estoque}</td>
                  <td className="text-right py-1.5 font-mono">{formatMoney(p.custo_unit)}</td>
                  <td className="text-right py-1.5 pr-3 font-mono font-semibold text-violet-700">{formatMoney(p.valor_custo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-red-50 border-b text-xs font-semibold uppercase text-red-700 flex items-center gap-1.5">
            <XCircle size={12} /> Em ruptura ({data.ruptura_total})
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {data.ruptura.map((p: any) => (
              <div key={p.id} className="flex justify-between p-2 border-t border-ink-100 text-xs hover:bg-red-50/30">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] text-ink-500">{p.codigo}</div>
                  <div className="truncate">{p.descricao}</div>
                </div>
                <div className="text-[10px] text-ink-500">{formatMoney(p.preco_venda)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
